<script>
    import Reporter from "../../lib/error.js";
    import { addOrUpdateContributor } from "../../Contributor.svelte";
    import ContributorModel from "../../models/participaid/Contributor.js";

    import Dialog from "../Dialog.svelte";
    import Name from "../Name.svelte";
    import Emoji from "../Emoji.svelte";
    import Field from "../form/Field.svelte";
    import TextField from "../form/TextField.svelte";
    import NumberField from "../form/NumberField.svelte";
    import UrlField from "../form/UrlField.svelte";
    //import ImageUploadField from "../form/ImageUploadField.svelte";
    import CloudinaryImageUpload from "../form/CloudinaryImageUpload.svelte";
    import Button from "../form/Button.svelte";
    import {Label} from '@smui/button';
    import Messages from "../form/Messages.svelte";
    import Message from "../form/Message.svelte";
    import LoadingModal from "../LoadingModal.svelte";
    import ContributorDropdown from "../form/ContributorDropdown.svelte";
    import Slider from "../form/Slider.svelte";
    import SalaryField from "../form/SalaryField.svelte";
    import ContributorInline from "../participaid/ContributorInline.svelte";

    import {Input, Textarea} from '@smui/textfield';
    import Icon from '@smui/textfield/icon/index';
    import HelperText from '@smui/textfield/helper-text/index';
    import {Icon as CommonIcon} from '@smui/common';
    import FloatingLabel from '@smui/floating-label';
    import LineRipple from '@smui/line-ripple';
    import NotchedOutline from '@smui/notched-outline';
    import Tab from '@smui/tab';
    import TabBar from '@smui/tab-bar';

    import { createEventDispatcher } from 'svelte';
    const dispatch = createEventDispatcher();

    const STANDARD_MARKET_ANNUAL_SALARY = 30000;
    const STANDARD_MARKET_HOURLY_SALARY = 50;

    // whether this dialog should be shown
    export let show = true;

    // project model
    export let projectId;

    // pass the contributor if it exists
    export let contributor;

    // whether a user is configuring himself
    export let isSelf = false;

    // whether the currently logged in user is the owner of the project
    export let isOwner = false;

    // submission request states
    let errorResult;
    let submitRequest;
    let errorMsg;

    let title = (isOwner && isSelf) ? "Project Owner" : (contributor ? contributor.title : "");
    let marketAnnualSalary = contributor ? (contributor.marketAnnualSalary/100 || STANDARD_MARKET_ANNUAL_SALARY) : STANDARD_MARKET_ANNUAL_SALARY;
    let hourlySalary = contributor ? (contributor.hourlySalary/100 || STANDARD_MARKET_HOURLY_SALARY) : STANDARD_MARKET_HOURLY_SALARY
    let currency = "euro";
    let commissionPercentage = contributor ? (contributor.commissionPercentage || 0) : 0;
    let startDate = (new Date()).toISOString();

    // interface switch that decides which component user sees for entering salary
    let rateTypeIsAnnually = false;

    $: isSelfOwner = isOwner && isSelf;
    $: titleIsPO = (title || "").toLowerCase().trim().includes("project owner");
    $: disabled = !title || (!rateTypeIsAnnually && !hourlySalary) || (titleIsPO && !isSelfOwner);
    $: salaryObj = {marketAnnualSalary: marketAnnualSalary*100, hourlySalary: hourlySalary*100};

    $: {
        console.log(`contributorData changed! Has contributor`);
        console.log(contributor);
    }

    $: contributorInitialized = contributor && ContributorModel.isInitialized(contributor);

    // register contributor for this project
    function create() {
        console.assert(!!projectId, "[ConfigureContributorDialog] no project id found!");
        // TODO: fix this issue: https://gitlab.com/eyevestor/evaluaid/app/-/issues/23
        if (!isSelfOwner && titleIsPO) {
            errorResult = "This title is reserved and not allowed for use.";
        }

        else {
            submitRequest = addOrUpdateContributor(projectId, {
                ...{
                    title, currency, commissionPercentage, startDate
                },
                ...salaryObj,

                // add the existing contributor id if a contributor exists for the updateContributor operation
                ...{id: contributor && contributor.id}
            })
            .then(res => {
                console.log("[ConfigureContributorDialog] successfully created or updated Contributor");
                console.log(res.data);
                if (contributor) {
                    dispatch("update", res.data.updateContributor);
                }
                else {
                    dispatch("new", res.data.addContributor);
                }
                errorResult = null;
                errorMsg = null;
            })
            .catch(res => {
                Reporter.error("[ConfigureContributorDialog] error trying to create or update Contributor", res);
                errorResult = res;
            })
        }
    }

</script>

<Dialog {show}>
    <!-- TODO: support avatar in contributor -->
<!--    <CloudinaryImageUpload-->
<!--            bind:url={avatarUrl}-->
<!--            cropRatio="1" />-->

    <h1>Configure contributor <Emoji name="pencil2"/></h1>
    <p style="display: flex; flex-direction: row; align-items: center; flex-flow: wrap; justify-content: center;">
        {#if contributor}
            <span>Configure how contributions should be valued from </span>
            <nbsp/>
            {#if isSelf}
                yourself
            {:else}
                <ContributorInline model={contributor} />
            {/if}
        {:else}
            Configure how contributions from this new contributor should be valued
        {/if}
    </p>
    <form>
        <Messages>
            {#if errorResult}
                <Message>{JSON.stringify(errorResult)}</Message>
            {/if}
        </Messages>
        {#if !isSelfOwner}
            <Field>
                <TextField
                        bind:value={title}
                        fullwidth
                        label="Role title"
                        placeholder="Lead developer"
                        help="Role title for this contributor. Can still be changed later. Examples: CTO, Marketeer, Advisor, etc. It is advised to choose an identifying label over a generic one."

                />
            </Field>
        {/if}
        <Field style="margin-bottom: 50px;">
            <SalaryField
                    bind:rateTypeIsAnnually
                    bind:marketAnnualSalary
                    bind:hourlySalary />
        </Field>
        <Field style="margin-bottom: 50px;">
<!--            <Slider-->
<!--                    min={0}-->
<!--                    max={100}-->
<!--                    bind:value={commissionPercentage}-->
<!--                    step={1}-->
<!--                    label="Commission percentage"-->
<!--                    help="The finder's fee earned for acquisition activities such as closing sales or investors"-->
<!--            />-->
            <NumberField
                    bind:value={commissionPercentage}
                    fullwidth
                    label="Commission percentage"
                    placeholder="0.0"
                    step={0.1}
                    help="The finder's fee earned for acquisition activities such as closing sales or bringing in investors. This value is only used when using entry fields that are configured to use the COMMISSION_CONTRIBUTOR formula type. Otherwise, the commission value is used that is defined in the field configuration. See the Agreements for an overview."

            />
        </Field>
    </form>
    <Button id="button-step1" on:click={create} {disabled} style="margin: auto; display: block;">
        <Label>
            {#if contributor}
                Update {#if isSelf}your{:else}contributor{/if} details
            {:else}
                Register as contributor
            {/if}
        </Label>
    </Button>

    <!-- show loading modal while request is in flight -->
    {#await submitRequest}
        <LoadingModal global={false}/>
    {/await}
</Dialog>

<style>
    form {
        margin: 25px 0;
    }

    p {
        text-align: center;
        margin: 25px 0;
    }

    :global(#button-step1) {
        margin: auto;
        //margin-top: 50px;
        display: block;
    }

</style>